import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { COLORS } from '../../theme/tokens';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  boundaryCount = 1,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (totalPages <= 1) {
    return null;
  }

  const buttonSize = isMobile ? 40 : 38;

  const getVisiblePages = () => {
    const pages = new Set();

    for (let i = 1; i <= Math.min(boundaryCount, totalPages); i += 1) {
      pages.add(i);
    }

    for (let i = Math.max(totalPages - boundaryCount + 1, 1); i <= totalPages; i += 1) {
      pages.add(i);
    }

    for (let i = currentPage - siblingCount; i <= currentPage + siblingCount; i += 1) {
      if (i >= 1 && i <= totalPages) {
        pages.add(i);
      }
    }

    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];

    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) {
        result.push('ellipsis');
      }
      result.push(page);
    });

    return result;
  };

  const handlePageClick = (page) => {
    if (page === currentPage) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onPageChange(page);
  };

  const pageButtonSx = (isActive) => ({
    width: buttonSize,
    height: buttonSize,
    minWidth: buttonSize,
    borderRadius: '2px',
    fontSize: '0.875rem',
    fontWeight: isActive ? 600 : 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isActive ? 'default' : 'pointer',
    border: isActive ? 'none' : `1px solid ${COLORS.borderControl}`,
    bgcolor: isActive ? COLORS.green : 'transparent',
    color: isActive ? '#FFFFFF' : COLORS.textPrimary,
    transition: 'border-color 0.2s ease',
    '&:hover': isActive
      ? {}
      : {
          borderColor: COLORS.green,
        },
  });

  const visiblePages = getVisiblePages();
  const navigationButtonSx = {
    width: buttonSize,
    height: buttonSize,
    borderRadius: '2px',
    border: `1px solid ${COLORS.borderControl}`,
    '&:hover': {
      borderColor: COLORS.green,
      bgcolor: 'transparent',
    },
  };

  return (
    <Box
      component="nav"
      aria-label="pagination navigation"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        my: 4,
        flexWrap: 'nowrap',
      }}
    >
      {!isMobile && currentPage > 1 && (
        <IconButton
          aria-label={t('common.firstPage')}
          onClick={() => handlePageClick(1)}
          sx={navigationButtonSx}
        >
          <FirstPageIcon sx={{ fontSize: 20, color: COLORS.textPrimary }} />
        </IconButton>
      )}

      {currentPage > 1 && (
        <IconButton
          aria-label={t('common.previousPage')}
          onClick={() => handlePageClick(currentPage - 1)}
          sx={navigationButtonSx}
        >
          <ChevronLeftIcon sx={{ fontSize: 20, color: COLORS.textPrimary }} />
        </IconButton>
      )}

      {visiblePages.map((item, index) => {
        if (item === 'ellipsis') {
          return (
            <Typography
              key={`ellipsis-${index}`}
              sx={{
                width: buttonSize,
                textAlign: 'center',
                color: COLORS.textSecondary,
                userSelect: 'none',
              }}
            >
              …
            </Typography>
          );
        }

        const isActive = item === currentPage;

        return (
          <Box
            key={item}
            component="button"
            type="button"
            aria-label={isActive ? `page ${item}` : `Go to page ${item}`}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => handlePageClick(item)}
            sx={pageButtonSx(isActive)}
          >
            {item}
          </Box>
        );
      })}

      {currentPage < totalPages && (
        <IconButton
          aria-label={t('common.nextPage')}
          onClick={() => handlePageClick(currentPage + 1)}
          sx={{
            ...navigationButtonSx,
            ml: '2px',
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 20, color: COLORS.textPrimary }} />
        </IconButton>
      )}

      {!isMobile && currentPage < totalPages && (
        <IconButton
          aria-label={t('common.lastPage')}
          onClick={() => handlePageClick(totalPages)}
          sx={navigationButtonSx}
        >
          <LastPageIcon sx={{ fontSize: 20, color: COLORS.textPrimary }} />
        </IconButton>
      )}
    </Box>
  );
};

export default Pagination;
